import { ResidencyBasis } from 'projects/borrower-app/src/app/models/enums/residency-basis.enum';
import { ResidencyAddress } from 'projects/borrower-app/src/app/models/residency-address.model';
import { AddressParts, normalizeAddressPart, reoMatchesAddress } from 'projects/borrower-app/src/app/wizard/reo-address-match.util';

/** LLS-638: street, city, state and ZIP are all filled in. */
export function isCompleteAddress(address: AddressParts | null | undefined): boolean {
  return [address?.address1, address?.city, address?.state, address?.zipCode]
    .every(part => normalizeAddressPart(part) !== '');
}

/**
 * LLS-638: true when the borrower's present address is the property being refinanced.
 *
 * Uses the normalized, null-safe LLS-140 matcher, and requires a complete present address so two
 * half-entered addresses that only share a street never count as the same property. Unit
 * (`address2`) is not compared because a residency `Address` has no unit field.
 */
export function isPresentAddressTheRefiProperty(
  presentAddress: ResidencyAddress | null | undefined,
  subjectProperty: AddressParts | null | undefined): boolean {
  return isCompleteAddress(presentAddress?.address) && reoMatchesAddress(presentAddress?.address, subjectProperty);
}

/**
 * LLS-638: the borrower confirmed the refinance property is their current address, so they own
 * it. Setting Own here (rather than only in the optional own/rent step) makes the Address History
 * screen show Ownership Status = Own and the REO card, whose "Do you plan on selling this
 * property?" already defaults to No ('Retained') in reo-properties.
 */
export function applyRefiSameAsCurrentAddressDefaults(presentAddress: ResidencyAddress): void {
  presentAddress.residencyBasis = ResidencyBasis.Own;
}

/**
 * LLS-638: when the borrower changes Yes to No (e.g. Yes -> Next -> Back -> No), clear an Own that
 * belongs to the refinance property. There is no record of who set it (this step, the Own/Rent
 * step, or the borrower), and it does not matter: No means the present address is about to be
 * replaced, so Own for the refinance property must not carry over to the new address. An Own for
 * any other address is left alone. Call this before the address is cleared.
 */
export function clearRefiSameAsCurrentAddressDefaults(
  presentAddress: ResidencyAddress,
  subjectProperty: AddressParts | null | undefined): void {
  if (presentAddress.residencyBasis === ResidencyBasis.Own
    && isPresentAddressTheRefiProperty(presentAddress, subjectProperty)) {
    presentAddress.residencyBasis = null;
  }
}
